// src/components/MonAgenda.jsx
//
// AGENDA DU CRÉATEUR
//
// Design harmonisé avec Dashboard
// - thème clair, cartes blanches, shadows, font Poppins
// - Le calendrier prend toute la largeur (plus de colonne fixe à droite)
// - Le détail d'un jour s'affiche dans une POPUP au clic

import React, { useEffect, useMemo, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    Loader2,
    Clock3,
    CheckCircle2,
    CalendarCheck2,
    CalendarX2,
    X,
} from "lucide-react";

import CalendrierCreateur from "./CalendrierCreateur";
import PanneauJourCreateur from "./PanneauJourCreateur";
import {
    cleDate,
    getAgendaCreateur,
} from "../services/AgendaCreateurService";

const MOIS = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const MonAgenda = () => {
    // ==================================================
    // ÉTAT
    // ==================================================

    const [parJour, setParJour] = useState({});
    const [compteurs, setCompteurs] = useState({});
    const [stats, setStats] = useState({});

    const [loading, setLoading] = useState(true);
    const [erreur, setErreur] = useState(null);

    const [decalage, setDecalage] = useState(0);

    const [selection, setSelection] = useState(null);
    const [evenementsJour, setEvenementsJour] = useState([]);

    // Popup ouverte ou non (indépendant de "selection" pour l'animation)
    const [popupOuverte, setPopupOuverte] = useState(false);

    // ==================================================
    // LE MOIS AFFICHÉ
    // ==================================================

    const moisAffiche = useMemo(() => {
        const base = new Date();
        base.setDate(1);
        base.setMonth(base.getMonth() + decalage);
        return {
            annee: base.getFullYear(),
            mois: base.getMonth(),
        };
    }, [decalage]);

    // ==================================================
    // CHARGEMENT
    // ==================================================

    useEffect(() => {
        const charger = async () => {
            setLoading(true);
            setErreur(null);
            setSelection(null);
            setEvenementsJour([]);
            setPopupOuverte(false);

            try {
                const debut = new Date(moisAffiche.annee, moisAffiche.mois, 1);
                const nbJours = new Date(moisAffiche.annee, moisAffiche.mois + 1, 0).getDate();
                const res = await getAgendaCreateur(debut, nbJours);

                setParJour(res.parJour);
                setCompteurs(res.compteurs);
                setStats(res.stats);
            } catch (e) {
                setErreur(e.message || "Impossible de charger l'agenda.");
            } finally {
                setLoading(false);
            }
        };

        charger();
    }, [moisAffiche]);

    // ==================================================
    // SÉLECTION — ouvre la popup
    // ==================================================

    const handleSelect = (date, evenements) => {
        setSelection(date);
        setEvenementsJour(evenements);
        setPopupOuverte(true);
    };

    const fermerPopup = () => setPopupOuverte(false);

    // ==================================================
    // RENDER
    // ==================================================

    return (
        <div className="w-full flex justify-center pb-20 font-Poppins">
            <div className="w-full max-w-4xl space-y-4">

                {/* ================= HEADER ================= */}
                <header>
                    <p className="text-xs sm:text-sm text-gray-500">
                        Visualisez votre programmation et repérez facilement
                        les dates disponibles. Cliquez sur un jour pour voir le détail.
                    </p>
                </header>

                {/* ================= STATISTIQUES ================= */}
                {!loading && Object.keys(stats).length > 0 && (
                    <div className="grid grid-cols-4 gap-2 sm:gap-3">
                        <AgendaStat icon={CalendarCheck2} label="À venir" valeur={stats.a_venir ?? 0} iconClass="bg-green-100 text-green-600" />
                        <AgendaStat icon={CheckCircle2} label="Terminés" valeur={stats.passes ?? 0} iconClass="bg-blue-100 text-blue-600" />
                        <AgendaStat icon={Clock3} label="Actifs" valeur={stats.actifs ?? 0} iconClass="bg-purple-100 text-purple-600" />
                        <AgendaStat icon={CalendarX2} label="Désactivés" valeur={stats.desactives ?? 0} iconClass="bg-gray-100 text-gray-500" />
                    </div>
                )}

                {/* ================= NAVIGATION MOIS — plus clair ================= */}
                <div className="bg-white shadow-sm rounded-2xl border border-gray-100 px-3 py-2.5 sm:px-4">
                    <nav className="flex items-center justify-between gap-3" aria-label="Navigation dans le calendrier">

                        <button
                            type="button"
                            onClick={() => setDecalage((d) => d - 1)}
                            aria-label="Mois précédent"
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 border border-gray-200 text-gray-600 hover:bg-color1 hover:text-white hover:border-color1 transition-all duration-200"
                        >
                            <ChevronLeft size={16} aria-hidden="true" />
                        </button>

                        <div className="text-center">
                            <div className="text-sm sm:text-base font-bold text-gray-900">
                                {MOIS[moisAffiche.mois]} {moisAffiche.annee}
                            </div>

                            {decalage !== 0 && (
                                <button
                                    type="button"
                                    onClick={() => setDecalage(0)}
                                    className="mt-0.5 text-[11px] font-semibold text-color1 hover:underline transition"
                                >
                                    Revenir à aujourd'hui
                                </button>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => setDecalage((d) => d + 1)}
                            aria-label="Mois suivant"
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 border border-gray-200 text-gray-600 hover:bg-color1 hover:text-white hover:border-color1 transition-all duration-200"
                        >
                            <ChevronRight size={16} aria-hidden="true" />
                        </button>

                    </nav>
                </div>

                {/* ================= CONTENU ================= */}
                {loading ? (
                    <div className="bg-white shadow-sm rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-14 gap-3" role="status" aria-live="polite">
                        <div className="flex items-center justify-center w-11 h-11 rounded-full bg-color1/10">
                            <Loader2 className="w-5 h-5 animate-spin text-color1" aria-hidden="true" />
                        </div>
                        <p className="text-gray-500 text-xs">Chargement de votre agenda…</p>
                    </div>

                ) : erreur ? (
                    <div className="bg-white shadow-sm rounded-2xl border border-gray-100 text-center py-20 px-6">
                        <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-red-100 text-red-500 mb-4">
                            <CalendarX2 size={26} />
                        </div>
                        <p className="text-gray-600 mb-5 text-sm sm:text-base">{erreur}</p>
                        <button
                            type="button"
                            onClick={() => setDecalage((d) => d)}
                            className="px-5 py-2.5 rounded-xl bg-color1 text-white text-sm font-semibold shadow-sm hover:opacity-90 transition"
                        >
                            Réessayer
                        </button>
                    </div>

                ) : (
                    // ================= CALENDRIER PLEINE LARGEUR =================
                    <div>
                        <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="p-2 sm:p-3">
                                <CalendrierCreateur
                                    annee={moisAffiche.annee}
                                    mois={moisAffiche.mois}
                                    parJour={parJour}
                                    compteurs={compteurs}
                                    selection={popupOuverte ? selection : null}
                                    onSelect={handleSelect}
                                />
                            </div>
                        </div>

                        {/* ================= LÉGENDE ================= */}
                        <div className="bg-white shadow-sm rounded-2xl border border-gray-100 mt-3 px-3 py-2.5">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] sm:text-xs text-gray-600">
                                <Legend color="bg-green-500" label="À venir" />
                                <Legend color="bg-blue-500" label="Terminé" />
                                <Legend color="bg-purple-500" label="Actif" />
                                <Legend color="bg-gray-400" label="Désactivé" />
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* ══════════════════════════════════════════════════════
                POPUP — Détail du jour (remplace la colonne fixe)
            ══════════════════════════════════════════════════════ */}
            {popupOuverte && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={fermerPopup}
                >
                    <div
                        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Bouton fermer */}
                        <button
                            onClick={fermerPopup}
                            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition z-10"
                            aria-label="Fermer"
                        >
                            <X size={18} />
                        </button>

                        <PanneauJourCreateur
                            date={selection}
                            evenements={evenementsJour}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================================================
// STATISTIQUE AGENDA
// ==================================================

const AgendaStat = ({ icon: Icon, label, valeur, iconClass }) => (
    <div className="flex flex-col bg-white shadow-sm rounded-xl p-2.5 transition-all duration-300 hover:shadow-md border border-gray-100">
        <div className="flex flex-row gap-1.5 items-center">
            <div className={`rounded-full p-1.5 ${iconClass}`}>
                <Icon size={14} />
            </div>
            <p className="text-[11px] sm:text-xs font-semibold text-gray-700 truncate">
                {label}
            </p>
        </div>
        <h3 className="text-lg sm:text-xl font-extrabold text-gray-900 mt-1">
            {valeur}
        </h3>
    </div>
);

// ==================================================
// LÉGENDE
// ==================================================

const Legend = ({ color, label }) => (
    <span className="flex items-center gap-2 font-medium">
        <span className={`w-2 h-2 rounded-full ${color}`} />
        {label}
    </span>
);

export default MonAgenda;